import React from "react";
import { useMetricMeta } from "@client/hooks/useMetricMeta";
import { t } from "@client/lib/dictionary";
import type { TracerChargingStage, TracerTelemetry } from "@client/types/tracer";
import { formatMetric } from "@client/utils/formatMetric";
import { Card, CardContent } from "@client/components/ui/card";

interface Props {
  slug: string;
  telemetry: TracerTelemetry | undefined;
}

interface NodeProps {
  label: string;
  primary: string;
  secondary?: string;
  emoji: string;
  active: boolean;
  className?: string;
}

const Node: React.FC<NodeProps> = ({ label, primary, secondary, emoji, active, className }) => (
  <div
    className={`flex flex-col items-center justify-center gap-1 rounded-2xl border-2 px-4 py-3 min-w-32 transition-all ${
      active ? "border-green-500 bg-green-500/10 shadow-lg shadow-green-500/20" : "border-border bg-muted/30 opacity-70"
    } ${className ?? ""}`}
  >
    <div className="text-2xl">{emoji}</div>
    <div className="text-xs font-medium text-muted-foreground">{label}</div>
    <div className="text-xl font-bold tabular-nums">{primary}</div>
    {secondary && <div className="text-xs tabular-nums text-muted-foreground">{secondary}</div>}
  </div>
);

interface ArrowProps {
  direction: "right" | "left" | "down" | "up";
  active: boolean;
  label?: string;
  /** Na mobilu (pod `sm`) otočí vodorovný glyf o 90° → svislý tok ve stacku. */
  stackRotate?: boolean;
}

const Arrow: React.FC<ArrowProps> = ({ direction, active, label, stackRotate }) => {
  const arrow = direction === "right" ? "→" : direction === "left" ? "←" : direction === "down" ? "↓" : "↑";
  const animClass = active ? "animate-pulse" : "opacity-30";
  const colorClass = active ? "text-green-500" : "text-muted-foreground";
  return (
    <div className={`flex flex-col items-center justify-center gap-1 ${animClass}`}>
      <span className={`text-3xl ${colorClass} ${stackRotate ? "rotate-90 sm:rotate-0" : ""}`}>{arrow}</span>
      {label && <span className="text-xs text-muted-foreground tabular-nums">{label}</span>}
    </div>
  );
};

const stageEmoji = (stage: TracerChargingStage): string => {
  switch (stage) {
    case "BOOST":
      return "⚡";
    case "FLOAT":
      return "🌊";
    case "EQUALIZE":
      return "⚖️";
    case "NO_CHARGE":
      return "🌙";
    default:
      return "🔆";
  }
};

export const TracerFlowDiagram: React.FC<Props> = ({ slug, telemetry }) => {
  const topic = (metric: string) => `zige/tracer-${slug}/${metric}/val`;
  const pvWMeta = useMetricMeta(topic("pv-w"));
  const pvVMeta = useMetricMeta(topic("pv-v"));
  const pvAMeta = useMetricMeta(topic("pv-a"));
  const chargeWMeta = useMetricMeta(topic("battery-charge-w"));
  const heatsinkMeta = useMetricMeta(topic("heatsink-temp"));
  const chargeAMeta = useMetricMeta(topic("battery-charge-a"));
  const netAMeta = useMetricMeta(topic("battery-net-a"));
  const socMeta = useMetricMeta(topic("battery-soc"));
  const batteryVMeta = useMetricMeta(topic("battery-v"));
  const batteryTempMeta = useMetricMeta(topic("battery-temp"));
  //const loadWMeta = useMetricMeta(topic("load-w"));
  //const loadVMeta = useMetricMeta(topic("load-v"));
  //const loadAMeta = useMetricMeta(topic("load-a"));

  if (!telemetry) {
    return (
      <Card>
        <CardContent className="p-12 text-center text-muted-foreground">Čekám na první telemetrii…</CardContent>
      </Card>
    );
  }

  const pvActive = telemetry.pvPowerW > 0.5;
  const chargingActive =
    telemetry.batteryChargeCurrentA > 0.05 &&
    (telemetry.chargingStage === "BOOST" ||
      telemetry.chargingStage === "FLOAT" ||
      telemetry.chargingStage === "EQUALIZE");
  //const loadActive = telemetry.loadPowerW > 0.5 || telemetry.loadOn;
  const netA = telemetry.batteryNetCurrentA;
  const dischargingNet = netA !== null && netA < -0.05;

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex flex-col items-center gap-2 md:gap-4">
          {/* Top row: PV */}
          <Node
            label="PV (Solar)"
            primary={formatMetric(telemetry.pvPowerW, pvWMeta)}
            secondary={`${formatMetric(telemetry.pvVoltageV, pvVMeta)} / ${formatMetric(telemetry.pvCurrentA, pvAMeta)}`}
            emoji="☀️"
            active={pvActive}
          />
          <Arrow
            direction="down"
            active={pvActive}
            label={pvActive ? formatMetric(telemetry.pvPowerW, pvWMeta) : undefined}
          />

          {/* Middle row: Charger ↔ Battery — na mobilu sloupec, od sm řádek */}
          <div className="flex flex-col sm:flex-row items-center gap-2 md:gap-4 w-full justify-center">
            <Node
              label={`Charger — ${t.tracer.stage[telemetry.chargingStage]}`}
              primary={formatMetric(telemetry.batteryChargePowerW, chargeWMeta)}
              secondary={`Heat ${formatMetric(telemetry.heatsinkTempC, heatsinkMeta)} · ${
                telemetry.running ? "Running" : "Standby"
              }`}
              emoji={stageEmoji(telemetry.chargingStage)}
              active={true}
              className="border-blue-500 bg-blue-500/10"
            />
            <Arrow
              direction={chargingActive ? "right" : dischargingNet ? "left" : "right"}
              active={chargingActive || dischargingNet}
              stackRotate
              label={
                chargingActive
                  ? `+${formatMetric(telemetry.batteryChargeCurrentA, chargeAMeta)}`
                  : dischargingNet && netA !== null
                    ? formatMetric(netA, netAMeta)
                    : undefined
              }
            />
            <Node
              label="Baterie"
              primary={formatMetric(telemetry.batterySocPercent, socMeta)}
              secondary={`${formatMetric(telemetry.batteryVoltageV, batteryVMeta)} · ${formatMetric(telemetry.batteryTempC, batteryTempMeta)}`}
              emoji="🔋"
              active={chargingActive || dischargingNet}
            />
          </div>

          {/*
          <Arrow
            direction="down"
            active={loadActive}
            label={loadActive ? formatMetric(telemetry.loadPowerW, loadWMeta) : undefined}
          />

          <Node
            label="Zátěž (Load)"
            primary={formatMetric(telemetry.loadPowerW, loadWMeta)}
            secondary={`${formatMetric(telemetry.loadVoltageV, loadVMeta)} · ${formatMetric(telemetry.loadCurrentA, loadAMeta)}`}
            emoji="💡"
            active={loadActive}
          />
          */}
        </div>
      </CardContent>
    </Card>
  );
};
